export interface PermissionDef {
  code: string;
  label: string;
  /** Optional human hint mapping the capability to what it unlocks in the app. */
  hint?: string;
}

export interface PermissionGroup {
  module: string;
  label: string;
  permissions: PermissionDef[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    module: "POS",
    label: "Punto de Venta",
    permissions: [
      { code: "POS_SALE_CREATE", label: "Crear ventas" },
      { code: "POS_DISCOUNT", label: "Aplicar descuentos" },
      { code: "POS_GIFT", label: "Regalar / cortesías", hint: "Marcar líneas como regalía (el límite de costo se controla en Configuración)" },
      { code: "POS_WHOLESALE_PRICE", label: "Usar precio mayorista" },
      { code: "POS_USE_RESELLER_PRICE", label: "Usar precio revendedor" },
      { code: "POS_VOID", label: "Anular ventas" },
      { code: "POS_RETURN", label: "Devolver articulos" },
      { code: "APPROVE_VOID_RETURN", label: "Aprobar anulaciones y devoluciones" },
      { code: "POS_CLOSE_REGISTER", label: "Abrir y cerrar caja" },
      { code: "POS_SELECT_SALESPERSON", label: "Seleccionar vendedor" },
      {
        code: "POS_SELL_SERIALIZED_ITEMS",
        label: "Vender articulos serializados (IMEI)",
      },
    ],
  },
  {
    module: "INVENTORY",
    label: "Inventario",
    permissions: [
      { code: "INVENTORY_VIEW", label: "Ver inventario" },
      { code: "INVENTORY_EDIT", label: "Editar inventario", hint: "Recibir mercadería, crear productos y editar existencias" },
      { code: "INVENTORY_ADJUST", label: "Solicitar ajustes" },
      { code: "INVENTORY_ADJUST_EXECUTE", label: "Ejecutar ajustes directos", hint: "Finalizar conteos físicos y aplicar ajustes sin aprobación" },
      { code: "INVENTORY_TRANSFER", label: "Transferir inventario" },
      { code: "INVENTORY_VIEW_COST", label: "Ver costos", hint: "Costos, márgenes y reportes financieros de inventario" },
      { code: "INVENTORY_VIEW_SUPPLIER", label: "Ver proveedor" },
      { code: "INVENTORY_VIEW_SERIALS", label: "Ver unidades serializadas (IMEI)" },
      { code: "INVENTORY_MANAGE_SERIALS", label: "Gestionar unidades serializadas" },
    ],
  },
  {
    module: "SALES",
    label: "Ventas e Historial",
    permissions: [
      { code: "SALES_VIEW", label: "Ver historial de ventas" },
      { code: "SALES_VIEW_SHIFT_ONLY", label: "Ver solo ventas del turno" },
      { code: "SALES_REPORT", label: "Generar reportes financieros", hint: "Rentabilidad, costos, compras y fugas — visibilidad de dinero" },
    ],
  },
  {
    module: "CRM",
    label: "CRM / Clientes",
    permissions: [
      { code: "CUSTOMERS_VIEW", label: "Ver clientes" },
      { code: "CUSTOMERS_EDIT", label: "Editar clientes" },
      { code: "CUSTOMERS_CREATE", label: "Crear clientes (desde POS)" },
      { code: "CUSTOMERS_PAYMENT", label: "Registrar pagos de clientes" },
    ],
  },
  {
    module: "QUOTES",
    label: "Cotizaciones",
    permissions: [
      { code: "QUOTE_VIEW", label: "Ver cotizaciones" },
      { code: "QUOTE_EDIT", label: "Crear y editar cotizaciones" },
      { code: "QUOTE_SEND", label: "Enviar cotizaciones" },
      { code: "QUOTE_CONVERT", label: "Convertir cotizacion a venta" },
      { code: "QUOTE_DELETE", label: "Eliminar cotizaciones" },
    ],
  },
  {
    module: "FINANCE",
    label: "Finanzas",
    permissions: [
      { code: "FINANCE_VIEW", label: "Ver finanzas / gastos", hint: "Gastos y cuentas por pagar" },
      { code: "FINANCE_EDIT", label: "Registrar / pagar gastos" },
    ],
  },
  {
    module: "ADMIN",
    label: "Administracion",
    permissions: [
      { code: "ADMIN_SETTINGS", label: "Configuracion general", hint: "Información del negocio y configuración administrativa" },
      { code: "SETTINGS_PAYMENT_CONFIG", label: "Configuracion de pagos" },
      {
        code: "SETTINGS_MANAGE_SALESPERSONS",
        label: "Gestionar vendedores comerciales",
      },
      { code: "TENANT_SETTINGS_EDIT", label: "Editar preferencias del tenant" },
      { code: "CASH_REGISTER_MANAGE", label: "Gestionar caja" },
    ],
  },
];

const ALL_PERMISSION_CODES = PERMISSION_GROUPS.flatMap((group) =>
  group.permissions.map((permission) => permission.code),
);

const OWNER_SCOPE_RESTRICTION_PERMISSIONS = new Set<string>([
  "SALES_VIEW_SHIFT_ONLY",
]);

const OWNER_DEFAULT_PERMISSIONS = new Set<string>(
  ALL_PERMISSION_CODES.filter(
    (permission) => !OWNER_SCOPE_RESTRICTION_PERMISSIONS.has(permission),
  ),
);

// Mirrors the backend operational MANAGER default (permissions.constants.ts):
// daily operations, NOT owner-equivalent money/admin. Financial/admin codes
// (SALES_REPORT, INVENTORY_VIEW_COST, FINANCE_*, ADMIN_USERS, ADMIN_SETTINGS)
// are withheld by default and granted explicitly by the OWNER. Used by the
// "Restaurar por rol" button and role-change pre-check.
const MANAGER_DEFAULT_PERMISSIONS = new Set<string>([
  "POS_SALE_CREATE",
  "POS_CLOSE_REGISTER",
  "POS_DISCOUNT",
  "POS_GIFT",
  "POS_SELECT_SALESPERSON",
  "POS_USE_RESELLER_PRICE",
  "POS_SELL_SERIALIZED_ITEMS",
  "INVENTORY_VIEW",
  "INVENTORY_EDIT",
  "INVENTORY_ADJUST",
  "INVENTORY_ADJUST_EXECUTE",
  "INVENTORY_TRANSFER",
  "INVENTORY_VIEW_SUPPLIER",
  "INVENTORY_VIEW_SERIALS",
  "INVENTORY_MANAGE_SERIALS",
  "SALES_VIEW",
  "CUSTOMERS_VIEW",
  "CUSTOMERS_EDIT",
  "CUSTOMERS_CREATE",
  "CUSTOMERS_PAYMENT",
  "QUOTE_VIEW",
  "QUOTE_EDIT",
  "QUOTE_SEND",
  "QUOTE_CONVERT",
  "QUOTE_DELETE",
  "SETTINGS_PAYMENT_CONFIG",
  "SETTINGS_MANAGE_SALESPERSONS",
  "TENANT_SETTINGS_EDIT",
  "CASH_REGISTER_MANAGE",
]);

const CASHIER_DEFAULT_PERMISSIONS = new Set<string>([
  "POS_SALE_CREATE",
  "POS_CLOSE_REGISTER",
  "POS_SELECT_SALESPERSON",
  "POS_SELL_SERIALIZED_ITEMS",
  "INVENTORY_VIEW",
  "INVENTORY_VIEW_SERIALS",
  "SALES_VIEW",
  "SALES_VIEW_SHIFT_ONLY",
  "CUSTOMERS_VIEW",
  "CUSTOMERS_CREATE",
  "CUSTOMERS_PAYMENT",
  "QUOTE_VIEW",
  "QUOTE_EDIT",
  "QUOTE_SEND",
  "QUOTE_CONVERT",
  "CASH_REGISTER_MANAGE",
]);

const PERMISSION_ACTION_LABELS: Record<string, string> = {
  INVENTORY_EDIT: "editar inventario",
  INVENTORY_ADJUST: "registrar stock inicial y ajustes de inventario",
  INVENTORY_ADJUST_EXECUTE: "aplicar ajustes directos de inventario",
  INVENTORY_MANAGE_SERIALS: "registrar IMEI y unidades serializadas",
  POS_SALE_CREATE: "crear ventas",
  POS_CREATE_SALE: "crear ventas",
  INVENTORY_VIEW_COST: "ver costos",
  INVENTORY_COSTS: "ver costos",
  INVENTORY_VIEW_SUPPLIER: "ver proveedor",
  POS_USE_RESELLER_PRICE: "usar precio revendedor",
  POS_ALLOW_PRICE_PAYMENT_MISMATCH:
    "mantener un precio distinto al metodo de pago",
  SETTINGS_MANAGE_SALESPERSONS: "gestionar vendedores",
  POS_SELECT_SALESPERSON: "seleccionar vendedor",
  SALES_VIEW_BY_SALESPERSON: "ver ventas por vendedor",
};

export function getDefaultPermissionsForRole(role: string): string[] {
  switch ((role || "").trim().toUpperCase()) {
    case "OWNER":
    case "ADMIN":
      return Array.from(OWNER_DEFAULT_PERMISSIONS);
    case "MANAGER":
      return Array.from(MANAGER_DEFAULT_PERMISSIONS);
    case "CASHIER":
      return Array.from(CASHIER_DEFAULT_PERMISSIONS);
    default:
      return [];
  }
}

export function getPermissionDeniedMessage(permissionCode?: string | null) {
  const normalized = (permissionCode || "").trim().toUpperCase();
  const action = PERMISSION_ACTION_LABELS[normalized];
  if (!action) {
    return "No tienes permiso para realizar esta accion. Solicita autorizacion a tu administrador.";
  }

  return `No tienes permiso para ${action}. Solicita autorizacion a tu administrador.`;
}
